export interface CalendarKindBehavior {
  /** Default duration used by create flows when the user has not provided one.
   *  `0` means all-day for the single-event form and no automatic end time
   *  when batch/AI text explicitly includes a clock time. */
  readonly defaultDurationMinutes?: number;
  readonly defaultReminderMinutesBefore?: number | null;
  readonly defaultRecurring?: boolean;
  readonly defaultRecurrence?: string | null;
  readonly aliases?: readonly string[];
}

export type CalendarKindBehaviorMap = Readonly<Record<string, CalendarKindBehavior>>;

export interface CalendarKindDefaults {
  readonly durationMinutes: number;
  readonly reminderMinutesBefore: number | null;
  readonly recurring: boolean;
  readonly recurrence: string | null;
}

export interface CalendarKindMatch {
  readonly kind: string;
  readonly alias: string;
  readonly index: number;
  readonly length: number;
}

const FALLBACK_KIND_BEHAVIOR: CalendarKindBehavior = {
  defaultDurationMinutes: 60,
  defaultReminderMinutesBefore: 15,
  defaultRecurring: false,
  defaultRecurrence: null,
  aliases: [],
};

export const DEFAULT_KIND_BEHAVIOR: CalendarKindBehaviorMap = {
  default: FALLBACK_KIND_BEHAVIOR,
  publish: {
    defaultDurationMinutes: 30,
    defaultReminderMinutesBefore: 60,
    aliases: [
      'publish',
      'issue',
      'essay',
      'article',
      'post',
      'release',
      'goes out',
      '发布',
      '发表',
      '文章',
      '稿件',
      '出刊',
    ],
  },
  newsletter: {
    defaultDurationMinutes: 45,
    defaultReminderMinutesBefore: 60,
    aliases: [
      'newsletter',
      'weekly note',
      'digest',
      'mailing',
      'briefing',
      '周记',
      '周报',
      '简报',
      '通讯',
      '邮件',
    ],
  },
  survey: {
    defaultDurationMinutes: 0,
    defaultReminderMinutesBefore: 60,
    aliases: [
      'survey',
      'poll',
      'reader letters',
      'questionnaire',
      'feedback',
      '问卷',
      '调查',
      '调研',
      '投票',
      '反馈',
    ],
  },
  thread: {
    defaultDurationMinutes: 60,
    defaultReminderMinutesBefore: 15,
    aliases: [
      'thread',
      'forum',
      'discussion',
      'crit',
      'prompt',
      '话题',
      '论坛',
      '讨论',
      '合议',
      '帖子',
    ],
  },
  workshop: {
    defaultDurationMinutes: 90,
    defaultReminderMinutesBefore: 60,
    aliases: [
      'workshop',
      'cohort',
      'session',
      'course',
      'class',
      '工作坊',
      '课程',
      '小节',
      '课',
    ],
  },
  deadline: {
    defaultDurationMinutes: 0,
    defaultReminderMinutesBefore: 1440,
    aliases: [
      'deadline',
      'due',
      'deliverable',
      'cutoff',
      '截止',
      '到期',
      '提交',
      '交付',
    ],
  },
  booking: {
    defaultDurationMinutes: 30,
    defaultReminderMinutesBefore: 15,
    aliases: [
      'booking',
      'book',
      'office hours',
      'appointment',
      'meeting',
      '1:1',
      '预约',
      '答疑',
      '面谈',
    ],
  },
};

export function resolveKindBehavior(
  map: CalendarKindBehaviorMap | undefined,
  kind: string,
): CalendarKindBehavior {
  return (
    map?.[kind] ??
    map?.default ??
    DEFAULT_KIND_BEHAVIOR[kind] ??
    DEFAULT_KIND_BEHAVIOR.default ??
    FALLBACK_KIND_BEHAVIOR
  );
}

export function resolveKindDefaults(
  map: CalendarKindBehaviorMap | undefined,
  kind: string,
  options?: {
    readonly fallbackDurationMinutes?: number;
    readonly fallbackReminderMinutesBefore?: number | null;
  },
): CalendarKindDefaults {
  const behavior = resolveKindBehavior(map, kind);
  const fallback = resolveKindBehavior(map, 'default');
  const durationMinutes =
    behavior.defaultDurationMinutes ??
    fallback.defaultDurationMinutes ??
    options?.fallbackDurationMinutes ??
    FALLBACK_KIND_BEHAVIOR.defaultDurationMinutes ??
    60;
  const reminderMinutesBefore =
    behavior.defaultReminderMinutesBefore !== undefined
      ? behavior.defaultReminderMinutesBefore
      : fallback.defaultReminderMinutesBefore !== undefined
        ? fallback.defaultReminderMinutesBefore
        : options?.fallbackReminderMinutesBefore ?? 15;
  return {
    durationMinutes: Math.max(0, durationMinutes),
    reminderMinutesBefore,
    recurring: behavior.defaultRecurring ?? fallback.defaultRecurring ?? false,
    recurrence: behavior.defaultRecurrence ?? fallback.defaultRecurrence ?? null,
  };
}

export function findKindBehaviorMatch(
  text: string,
  kindKeys: readonly string[],
  map: CalendarKindBehaviorMap | undefined,
): CalendarKindMatch | null {
  const candidates = kindKeys
    .filter((kind) => kind !== 'default')
    .flatMap((kind) =>
      [kind, ...(resolveKindBehavior(map, kind).aliases ?? [])].map((alias) => ({
        kind,
        alias,
      })),
    )
    .filter((candidate) => candidate.alias.trim().length > 0)
    .sort((a, b) => b.alias.length - a.alias.length);

  for (const candidate of candidates) {
    const match = matchAlias(text, candidate.alias);
    if (match !== null) {
      return { kind: candidate.kind, alias: candidate.alias, ...match };
    }
  }
  return null;
}

export function inferKindFromText(
  text: string,
  kindKeys: readonly string[],
  map: CalendarKindBehaviorMap | undefined,
  fallbackKind: string,
): string {
  return findKindBehaviorMatch(text, kindKeys, map)?.kind ?? fallbackKind;
}

function matchAlias(
  text: string,
  alias: string,
): { readonly index: number; readonly length: number } | null {
  const escaped = escapeRegex(alias.trim());
  const re = /[a-z0-9]/i.test(alias)
    ? new RegExp(`(^|[^\\p{L}\\p{N}])(${escaped})(?=$|[^\\p{L}\\p{N}])`, 'iu')
    : new RegExp(`(${escaped})`, 'iu');
  const found = re.exec(text);
  if (found === null) return null;
  const value = found[2] ?? found[1];
  if (value === undefined) return null;
  return {
    index: found.index + found[0].indexOf(value),
    length: value.length,
  };
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
